import { faSpinnerThird, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { useGetCurrentValueOfFieldInGroup } from '@uturn/api/v2';

import { useAddShipmentStore } from '@uturn/portal/store/shipments/add-shipment';
import { unsanitize } from '@uturn/portal/utils/unsanitize';
import {
	Button,
	FormField,
	Icon,
	Input,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@uturn/ui-kit';
import { useEffect, useMemo } from 'react';
import {
	type Control,
	Controller,
	useFormContext,
	useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FIELD_KEYS, isPublishStateFieldKey } from './field-keys';
import { CanAcceptSelect } from './inputs/can-accept-select';
import { CarriersMultiSelect } from './inputs/carriers-multi-select';
import { DateInput } from './inputs/date-input';
import { LocationInput } from './inputs/location-input';
import { PriceInput } from './inputs/price-input';
import { PublishStateSelect } from './inputs/publish-state-select';
import { TextInput } from './inputs/text-input';
import type { UpdateFilters } from './multi-update-dialog';
import type {
	FieldOption,
	InputRenderProps,
	InputType,
	MultiUpdateFormValues,
	Primitive,
} from './multi-update-types';
import {
	buildFixedPriceOptions,
	buildPublishStateOptions,
	formatCurrentValue,
	getDefaultValueForType,
	PUBLISH_STATE_BASELINE,
	toPrimitiveArray,
} from './multi-update-utils';

const PUBLISH_STATE_LABELS = {
	[FIELD_KEYS.PUBLISH]: 'Publish',
	[FIELD_KEYS.UNPUBLISH]: 'Unpublish',
} as const;

// Resolves the "Current value" cell: publish-state is derived from the key, others use stored/fetched values.
const resolveDisplayCurrentValues = ({
	isPublishStateField,
	fieldKey,
	rowCurrentValues,
	fetchedCurrentValues,
}: {
	isPublishStateField: boolean;
	fieldKey: unknown;
	rowCurrentValues: unknown;
	fetchedCurrentValues: unknown;
}): Primitive[] => {
	if (isPublishStateField && isPublishStateFieldKey(fieldKey)) {
		return [PUBLISH_STATE_BASELINE[fieldKey]];
	}
	if (Array.isArray(rowCurrentValues) && rowCurrentValues.length > 0) {
		return rowCurrentValues as Primitive[];
	}
	return Array.isArray(fetchedCurrentValues)
		? (fetchedCurrentValues as Primitive[])
		: [];
};

type Props = {
	index: number;
	fieldOptions: FieldOption[];
	control: Control<MultiUpdateFormValues>;
	onRemove: (index: number) => void;
	canRemove: boolean;
	groupStatus: 'assigned' | 'unassigned';
	groupId: number;
	filters: UpdateFilters;
	fieldOptionsLoading?: boolean;
};

export const FieldRow = ({
	index,
	fieldOptions,
	control,
	onRemove,
	canRemove,
	groupStatus,
	groupId,
	filters,
	fieldOptionsLoading = false,
}: Props) => {
	const { clearErrors, setValue, getValues } = useFormContext();
	const watchedField = useWatch({
		control,
		name: `rows.${index}.key`,
	});

	const { t } = useTranslation();
	const rowCurrentValues = useWatch({
		control,
		name: `rows.${index}.currentValues`,
		defaultValue: [],
	});

	const hasStored =
		Array.isArray(rowCurrentValues) && rowCurrentValues.length > 0;

	const isPublishStateField = isPublishStateFieldKey(watchedField);

	const {
		data: currentValues,
		isLoading,
		isFetching,
	} = useGetCurrentValueOfFieldInGroup(
		groupId,
		watchedField,
		{
			status: groupStatus,
			...filters,
		},
		{
			query: {
				select: toPrimitiveArray,
				enabled:
					!!groupId && !!watchedField && !hasStored && !isPublishStateField,
			},
		},
	);

	// to copy server “currentValues” into the form state once the data arrives. It also disables future refetches.
	// biome-ignore lint/plugin/no-use-effect: must re-run when the current-values query resolves, so it can't be a mount effect
	useEffect(() => {
		if (!watchedField) {
			return;
		}
		if (hasStored) {
			return;
		}
		if (!currentValues?.length) {
			return;
		}

		if (isFetching) {
			return;
		}

		// ensure the user didn't change the field while the query was in flight
		const stillThisField = getValues(`rows.${index}.key`);
		if (stillThisField !== watchedField) {
			return;
		}
		setValue(`rows.${index}.currentValues`, currentValues, {
			shouldDirty: false,
			shouldValidate: false,
		});
	}, [
		watchedField,
		hasStored,
		currentValues,
		getValues,
		index,
		setValue,
		isFetching,
	]);

	const { currencyCode } = useAddShipmentStore();

	const fixedPriceOptions = useMemo(() => buildFixedPriceOptions(t), [t]);

	const publishStateOptions = useMemo(() => buildPublishStateOptions(t), [t]);

	const allFields = useWatch({ control, name: 'rows' }) || [];

	const availableOptions = fieldOptions.filter((opt) => {
		const isAlreadySelected = allFields.some(
			(item, i: number) => item.key === opt.name && i !== index,
		);
		return !isAlreadySelected || opt.name === watchedField;
	});

	const selectedFieldOption = fieldOptions.find(
		(opt) => opt.name === watchedField,
	);
	const inputKey = `${index}:${watchedField ?? 'none'}:${selectedFieldOption?.inputType ?? 'text'}`;

	const displayCurrentValues = resolveDisplayCurrentValues({
		isPublishStateField,
		fieldKey: watchedField,
		rowCurrentValues,
		fetchedCurrentValues: currentValues,
	});

	const handleFieldChange = (onChange: (v: string) => void, val: string) => {
		onChange(val);
		const next = fieldOptions.find((o) => o.name === val);
		const defaultValue = getDefaultValueForType(next?.inputType);

		const nextCurrentValues = isPublishStateFieldKey(val)
			? [PUBLISH_STATE_BASELINE[val]]
			: [];

		// hard clear both "value" and "currentValues"
		setValue(`rows.${index}.value`, defaultValue, {
			shouldDirty: false,
			shouldValidate: false,
			shouldTouch: false,
		});
		setValue(`rows.${index}.currentValues`, nextCurrentValues, {
			shouldDirty: false,
			shouldValidate: false,
			shouldTouch: false,
		});
		setValue(`rows.${index}.locationSignature`, '', {
			shouldDirty: false,
			shouldValidate: false,
			shouldTouch: false,
		});
		clearErrors(`rows.${index}.value`);
	};

	const Inputs: Record<string, (props: InputRenderProps) => JSX.Element> = {
		boolean: ({ field }) => (
			<CanAcceptSelect
				field={field}
				placeholder={
					t(
						'pages.multi-update-shipments.inputs.select-value',
						'Select value',
					) ?? ''
				}
			/>
		),
		date: ({ field }) => <DateInput field={field} />,

		price: ({ field, currencyCode }) => (
			<PriceInput
				field={field}
				currencyCode={currencyCode ?? 'EUR'}
				placeholder={
					t(
						'pages.multi-update-shipments.price-input.new-value',
						'New value',
					) ?? ''
				}
			/>
		),
		location: ({ field }) => (
			<LocationInput
				field={field}
				placeholder={
					t(
						'components.shipment_location.placeholder',
						'Search for a name or address',
					) ?? ''
				}
				emptyMessage={t(
					'components.shipment_location.no-result',
					'No location found',
				)}
			/>
		),
		'multi-select': ({ field }) => <CarriersMultiSelect field={field} />,
		text: ({ field }) => (
			<TextInput
				id={`${field.name}-text-input`}
				field={field}
				placeholder={
					t('pages.multi-update-shipments.text-input.new-value', 'New value') ??
					''
				}
			/>
		),
	};

	const renderByType = (
		type: InputType | undefined,
		props: InputRenderProps,
	) => {
		if (!type) {
			return <TextInput field={props.field} />;
		}
		if (Inputs[type]) {
			return Inputs[type](props);
		}

		return <TextInput field={props.field} />;
	};

	return (
		<div className="mb-4 grid grid-cols-[1fr_1fr_1fr_auto] items-start gap-4">
			<Controller
				control={control}
				name={`rows.${index}.key`}
				render={({ field }) => (
					<Select
						value={field.value}
						onValueChange={(val) => handleFieldChange(field.onChange, val)}
						disabled={fieldOptionsLoading}
					>
						<SelectTrigger
							className="bg-white"
							id="multi-update-field-selector"
						>
							<SelectValue
								placeholder={t(
									'pages.multi-update-shipments.selector-label.select-field',
									'Select the field',
								).toString()}
							/>
						</SelectTrigger>
						<SelectContent>
							{availableOptions.map((opt) => (
								<SelectItem key={opt.name} value={opt.name}>
									{t(
										`pages.multi-update-shipments.select-options.${opt.name}`,
										isPublishStateFieldKey(opt.name)
											? PUBLISH_STATE_LABELS[opt.name]
											: unsanitize(opt.name),
									)}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				)}
			/>

			<div className="relative">
				<Input
					readOnly
					value={
						formatCurrentValue(
							displayCurrentValues,
							selectedFieldOption?.inputType ?? 'text',
							currencyCode,
							isPublishStateField ? publishStateOptions : fixedPriceOptions,
						) ?? ''
					}
				/>
				{isLoading && (
					<div className="w-full h-full opacity-50 bg-gray-50 absolute top-0 left-0 flex items-center justify-center">
						<Icon icon={faSpinnerThird} spin className="text-gray-400" />
					</div>
				)}
			</div>

			<FormField
				key={inputKey}
				control={control}
				name={`rows.${index}.value` as const}
				render={({ field }) => {
					if (!selectedFieldOption) {
						return (
							<Input
								disabled
								placeholder={t(
									'pages.multi-update-shipments.placeholder.select-field-first',
									'Select field first',
								).toString()}
							/>
						);
					}

					const inputProps: InputRenderProps = {
						field,
						t,
						currencyCode,
						watchedField,
					};

					if (isPublishStateField) {
						return (
							<PublishStateSelect
								field={inputProps.field}
								options={publishStateOptions}
								placeholder={
									t(
										'pages.multi-update-shipments.inputs.select-value',
										'Select value',
									) ?? ''
								}
							/>
						);
					}

					return renderByType(selectedFieldOption.inputType, inputProps);
				}}
			/>
			{canRemove && (
				<Button
					type="button"
					variant="ghost"
					size="icon"
					onClick={() => onRemove(index)}
				>
					<Icon icon={faTrash} className="text-red-600" />
				</Button>
			)}
		</div>
	);
};
